import React from 'react';
import { Activity, Clock, Loader2, Check, Circle } from 'lucide-react';
import { STEPS } from '../data/mock';

const STORAGE_KEY = 'cf_next_launch_at_ms';

const LiveActivity = ({ status, latestLaunch }) => {
  const formatETA = (seconds) => {
    const s = Math.max(0, Number(seconds || 0));
    const mins = Math.floor(s / 60);
    const secs = s % 60;
    return `${mins}m ${secs}s`;
  };

  const getStoredTargetMs = () => {
    try {
      const v = Number(window.localStorage.getItem(STORAGE_KEY) || 0);
      return Number.isFinite(v) && v > 0 ? v : null;
    } catch {
      return null;
    }
  };

  const computeEtaFromTargetMs = (targetMs) => {
    if (!targetMs) return 0;
    return Math.max(0, Math.round((targetMs - Date.now()) / 1000));
  };

  // countdown target
  const [targetMs, setTargetMs] = React.useState(() => {
    const sMs = typeof status?.next_cycle_at_ms === 'number' ? status.next_cycle_at_ms : null;
    return sMs || getStoredTargetMs();
  });

  const [eta, setEta] = React.useState(() => computeEtaFromTargetMs(targetMs));

  React.useEffect(() => {
    const sMs = typeof status?.next_cycle_at_ms === 'number' ? status.next_cycle_at_ms : null;
    if (sMs && Number.isFinite(sMs)) {
      setTargetMs(sMs);
      try {
        window.localStorage.setItem(STORAGE_KEY, String(sMs));
      } catch {}
    }
  }, [status?.next_cycle_at_ms]);

  React.useEffect(() => {
    setEta(computeEtaFromTargetMs(targetMs));
    const t = setInterval(() => {
      setEta(computeEtaFromTargetMs(targetMs));
    }, 1000);
    return () => clearInterval(t);
  }, [targetMs]);

  // Determine if a cycle is currently running.
  const steps = Array.isArray(status?.steps) ? status.steps : [];
  const hasRunningStep = steps.some((s) => s?.status === 'running');
  const showSteps = hasRunningStep;

  // Show a short "success" banner when a cycle finishes and we have a deployed token.
  const prevHadRunningRef = React.useRef(false);
  const [successLaunch, setSuccessLaunch] = React.useState(null);

  React.useEffect(() => {
    const prev = prevHadRunningRef.current;
    if (prev && !hasRunningStep) {
      if (latestLaunch?.contract_url) {
        setSuccessLaunch(latestLaunch);
        const t = setTimeout(() => setSuccessLaunch(null), 12_000);
        return () => clearTimeout(t);
      }
    }
    prevHadRunningRef.current = hasRunningStep;
  }, [hasRunningStep, latestLaunch]);

  const currentStepLabel = STEPS.find((s) => s.key === status?.current_step)?.label || status?.current_action || 'Working';

  const getStepIcon = (stepStatus) => {
    switch (stepStatus) {
      case 'done':
        return <Check className="w-3 h-3 text-white" />;
      case 'running':
        return <Loader2 className="w-3 h-3 text-white animate-spin" />;
      default:
        return <Circle className="w-2 h-2 text-white/30" />;
    }
  };

  const getStepBg = (stepStatus) => {
    switch (stepStatus) {
      case 'done':
        return 'bg-[#ff3b3b]';
      case 'running':
        return 'bg-[#ff3b3b] animate-pulse-red';
      default:
        return 'bg-white/10';
    }
  };

  if (showSteps) {
    return (
      <section id="live" className="py-16 px-6 relative z-10">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-lg bg-[#ff3b3b]/20">
              <Activity className="w-5 h-5 text-[#ff3b3b]" />
            </div>
            <h2 className="text-3xl font-display font-bold text-white">Live Cycle</h2>
          </div>

          <div className="glass-card p-6 md:p-7">
            <div className="flex items-start justify-between mb-6 gap-4">
              <div className="min-w-0">
                <p className="text-white/40 text-sm uppercase tracking-wider mb-2">Current Action</p>
                <h3 className="text-2xl font-display font-semibold text-white">{currentStepLabel}</h3>
              </div>
              <div className="flex items-center gap-2 text-white/40 bg-white/5 px-3 py-2 rounded-lg">
                <Clock className="w-4 h-4" />
                <span className="text-sm">Next: {formatETA(eta)}</span>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {steps.slice(0, 10).map((step, index) => {
                const stepInfo = STEPS.find((s) => s.key === step.name);
                const humanize = (k) => String(k || '')
                  .toLowerCase()
                  .replace(/_/g, ' ')
                  .replace(/\b\w/g, (c) => c.toUpperCase());
                const shortLabel = stepInfo?.label.split(' ').slice(0, 2).join(' ') || humanize(step.name);

                return (
                  <div
                    key={step.name}
                    className={`p-3 rounded-lg transition-all ${
                      step.status === 'running' ? 'bg-[#ff3b3b]/20 border border-[#ff3b3b]/30' : 'bg-white/5'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center ${getStepBg(step.status)}`}>
                        {getStepIcon(step.status)}
                      </div>
                      <span className="text-xs text-white/40">{String(index + 1).padStart(2, '0')}</span>
                    </div>
                    <p
                      className={`text-xs font-medium truncate ${
                        step.status === 'running' ? 'text-[#ff3b3b]' : step.status === 'done' ? 'text-white' : 'text-white/40'
                      }`}
                    >
                      {shortLabel}
                    </p>

                    {/* Per-step progress */}
                    <div className="mt-2 h-1.5 bg-white/10 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ease-out ${
                          step.status === 'done'
                            ? 'bg-[#ff3b3b]'
                            : step.status === 'running'
                              ? 'bg-gradient-to-r from-[#ff3b3b] to-[#ff6b6b]'
                              : 'bg-white/20'
                        }`}
                        style={{ width: `${Math.max(0, Math.min(100, Number(step.progress || 0)))}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="live" className="py-16 px-6 relative z-10">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 rounded-lg bg-[#ff3b3b]/20">
            <Clock className="w-5 h-5 text-[#ff3b3b]" />
          </div>
          <h2 className="text-3xl font-display font-bold text-white">Next Launch</h2>
        </div>

        {successLaunch ? (
          <div className="glass-card p-5 mb-4 border border-emerald-500/25 bg-emerald-500/5">
            <p className="text-emerald-200 text-sm font-semibold mb-1">Token successfully created</p>
            <p className="text-white/60 text-sm">
              {successLaunch.name} (${successLaunch.ticker}) is live.
              {successLaunch.contract_url ? (
                <a
                  href={successLaunch.contract_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-2 text-emerald-200 hover:text-emerald-100 underline underline-offset-4"
                >
                  View on BaseScan
                </a>
              ) : null}
            </p>
          </div>
        ) : null}

        <div className="glass-card p-6 md:p-7 flex items-center justify-between gap-6">
          <div className="min-w-0">
            <p className="text-white/50 text-sm mb-2">Next token launch in</p>
            <p className="text-4xl font-display font-bold text-white leading-none">{formatETA(eta)}</p>
            <p className="text-white/35 text-xs mt-3">Cadence target: ~10m</p>
          </div>

          <div className="relative w-20 h-20 shrink-0">
            <div className="absolute inset-0 rounded-full border border-white/10" />
            <div className="absolute inset-0 rounded-full border-2 border-[#ff3b3b]/30" />
            <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-[#ff3b3b] animate-spin" />
            <div className="absolute inset-3 rounded-full bg-[#ff3b3b]/10 blur-md" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default LiveActivity;
